import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroBanner from '@/components/feature/HeroBanner';
import CategoryTabs from '@/components/feature/CategoryTabs';
import MovieCard from '@/components/feature/MovieCard';
import { movies, hotMovies, newMovies, getMoviesByCategory } from '@/mocks/movies';

export default function Home() {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredMovies = useMemo(() => {
    let result = activeCategory === 'all' ? movies : getMoviesByCategory(activeCategory);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.description.toLowerCase().includes(q) ||
          m.actors.some((a) => a.toLowerCase().includes(q)) ||
          m.director.toLowerCase().includes(q)
      );
    }
    return result;
  }, [activeCategory, searchQuery]);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '天美影视',
    url: 'https://www.tianmeiys.com/',
    description: '天美影视高清免费在线观看，不缓冲不卡顿流畅追剧，免费看最新高清电影电视剧全集，在线观看高清影视大全，流畅体验。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.tianmeiys.com/?search={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '天美影视',
    url: 'https://www.tianmeiys.com/',
    description: '天美影视高清免费在线观看平台',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <HeroBanner />

        <section className="w-full px-4 md:px-6 py-8 md:py-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                {searchQuery ? `搜索: "${searchQuery}"` : '影视分类'}
              </h2>
              {searchQuery && (
                <span className="text-sm text-foreground-400">
                  共找到 {filteredMovies.length} 部作品
                </span>
              )}
            </div>
            <CategoryTabs activeCategory={activeCategory} onCategoryChange={setActiveCategory} />

            <div className="mt-6">
              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {filteredMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-search-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-400">未找到相关影视作品</p>
                  <p className="text-sm text-foreground-300 mt-1">请尝试其他搜索关键词</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {!searchQuery && hotMovies.length > 0 && (
          <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100/50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                  <i className="ri-fire-line text-accent-500 mr-2"></i>
                  热门推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {hotMovies.slice(0, 6).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>
        )}

        {!searchQuery && newMovies.length > 0 && (
          <section className="w-full px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                  <i className="ri-sparkling-line text-primary-500 mr-2"></i>
                  最新上线
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {newMovies.slice(0, 6).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </main>
    </>
  );
}